/**
 * <QuestCard> — une quête de l'élève, à l'étape où il en est (organism).
 *
 * La « Quête du jour » du livre (chap. 16), avec ce qu'une quête est dans l'app : celui qui la
 * porte et l'étape (« Quête d'Abou · 3/10 »), la phrase de l'étape, son avancement, et ce qu'elle
 * rapporte, des pétales et parfois un titre. Pas d'XP : une quête n'en donne pas.
 *
 * Trois états, tous lus sur la quête : en cours (la barre se remplit en corail, vide tant que rien
 * n'est fait), terminée (« vert pour l'acquis » : la barre et la chip), pas encore débloquée (une
 * étape à venir : rien ne s'allume, la phrase passe en encre secondaire).
 *
 * Le jeu se dessine plein (décision de Wajih, 15/10) : ce qui se gagne en glyphes du jeu (la chip
 * de gain à pétale, le titre en médaille), le nombre de l'avancement un cran au-dessus du texte,
 * celui qui porte la quête à la taille de liste du livre, et l'aurore sur une étape menée au bout
 * (« là où un champion a agi »).
 *
 * La carte ne se teinte jamais à la couleur de celui qui porte la quête : les couleurs des
 * champions sont des costumes, pas des signaux. Il se pose dans la fente `champion`, par
 * `<QuestChampion>` : sa tête à 54, à 72 en tête d'écran.
 *
 * Avec `onPress`, toute la carte ouvre la quête, comme aujourd'hui. `actionLabel` remplace ce
 * geste par le bouton-poussoir du livre, sur le même `onPress`.
 *
 * `embedded` la pose dans une autre surface (l'accusé d'un achat, dans sa feuille) : sans ombre,
 * sur le fond des encarts. Sous l'aurore, cet encart n'est plus plat : ses chips gardent leur tinte
 * (`hooks/useOnInset`).
 *
 * `aurora` pose l'aurore sur une étape en cours : un champion vient d'agir sur cette carte
 * (l'accusé d'un geste fait avancer la quête de Tina). Jamais sur toutes les cartes d'une liste.
 *
 * `fromCount` anime l'avancée que le geste vient de donner, comme l'ancienne quête de l'accusé
 * (`ProgressBarM`, `previous_count` → `count`) : la barre et son nombre partent de là et rejoignent
 * le compte un instant après, et `onFilled` le dit (l'écran y joue son son). Moins d'animation
 * demandé : la barre rejoint le compte tout de suite. Absent : la barre est au compte, immobile.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ProgressBar } from "../../atoms/ProgressBar"
import { SegmentedBar, STEP_BAR_HEIGHT, stepSegments } from "../../atoms/SegmentedBar"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { AuroraFill } from "../../molecules/AuroraFill"
import { Card } from "../../molecules/Card"
import { GainChip } from "../../molecules/GainChip"
import { TitleMedal } from "../../molecules/TitleMedal"
import { useFilledCount } from "../../hooks/useFilledCount"
import { InsetContext } from "../../hooks/useOnInset"
import { isInsetSurface, type SemanticColorKey } from "../../theme/tokens/colors"
import { dimming } from "../../theme/tokens/effects"
import { type UIQuest, type UIQuestStatus } from "../../../ports/primary/ui/types/studentHome"

/** La quête et son étape (`ports/primary/ui/types/studentHome`), réexportées pour leurs lecteurs du DS. */
export type { UIQuest, UIQuestStatus }

export type QuestCardProps = {
    quest: UIQuest
    /** Celui qui porte la quête : `<QuestChampion>`. Absent : rien. */
    champion?: React.ReactNode
    /** Ouvre la quête. Absent : la carte ne presse pas. */
    onPress?: () => void
    /** Le libellé du bouton-poussoir, qui prend alors `onPress` à la place de la carte. */
    actionLabel?: string
    /** Encastrée dans une autre surface : sans ombre, sur le fond des encarts. Défaut `false`. */
    embedded?: boolean
    /** L'aurore sur une étape en cours : un champion vient d'agir ici. Défaut `false`. */
    aurora?: boolean
    /** Le compte d'avant le geste : la barre en part et rejoint le compte. Absent : pas d'animation. */
    fromCount?: number | null
    /** La barre vient de rejoindre le compte. */
    onFilled?: () => void
    testID?: string
}

/** La place de celui qui porte la quête : la taille de liste du livre. */
export const QUEST_CHAMPION_SIZE = 54
/** Sa place en tête d'un écran (le détail de sa quête) : la plus grande du livre. */
export const QUEST_CHAMPION_HERO_SIZE = 72

export type QuestChampionProps = {
    name: ChampionName
    /** En tête d'un écran. Défaut `false`. */
    hero?: boolean
}

/** Celui qui porte la quête : sa tête, à la taille de la carte où il se pose. */
export function QuestChampion(props: QuestChampionProps) {
    const { name, hero = false } = props
    return <ChampionAvatar name={name} size={hero ? QUEST_CHAMPION_HERO_SIZE : QUEST_CHAMPION_SIZE} />
}

/** Au-delà, les pastilles deviennent des traits : la barre passe en piste continue. */
const QUEST_STEPS_MAX = 12

const TEXTS = {
    done: "Terminée",
    locked: "Pas encore débloquée",
}

export function QuestCard(props: QuestCardProps) {
    const {
        quest,
        champion,
        onPress,
        actionLabel,
        embedded = false,
        aurora = false,
        fromCount,
        onFilled,
        testID,
    } = props
    const { label, step, stepCount, description, total, todayLabel, petals, titleGained, status } = quest
    const count = useFilledCount(quest.count, fromCount, onFilled)

    const locked = status === "locked"
    const done = status === "done"
    const hasAction = actionLabel != null && actionLabel !== "" && onPress != null
    const pressable = onPress != null && !hasAction
    const hasToday = todayLabel != null && todayLabel !== "" && status === "current"
    const hasTitle = titleGained != null && titleGained !== ""
    const hasPetals = petals != null && petals > 0
    const auroraShown = aurora || done
    const background: SemanticColorKey = embedded ? "surface-subtle" : "surface-raised"

    return (
        <Card
            testID={testID}
            p="4"
            pressable={pressable}
            onPress={pressable ? onPress : undefined}
            elevation={embedded ? "none" : "card"}
            background={background}
        >
            {auroraShown && <AuroraFill />}
            <Stack gap="3">
                <Row gap="3" align="start">
                    {champion != null && <View style={[styles.champion, locked && styles.dimmed]}>{champion}</View>}
                    <Stack gap="1" style={styles.body}>
                        <SectionLabel>{`${label} · ${step}/${stepCount}`}</SectionLabel>
                        <Text size="text-lg" weight="bold" color={locked ? "text-muted" : "text-primary"}>
                            {description}
                        </Text>
                        {hasToday && (
                            <Text size="text-sm" color="text-muted">
                                {todayLabel}
                            </Text>
                        )}
                    </Stack>
                    {pressable && (
                        <View style={styles.chevron}>
                            <Icon name="chevron-right" size={16} color="text-muted" />
                        </View>
                    )}
                </Row>

                <Row gap="3">
                    <View style={styles.bar}>
                        <QuestBar count={locked ? 0 : count} total={total} done={done} />
                    </View>
                    <Text size="text-lg" weight="bold" color={locked ? "text-muted" : "text-primary"}>
                        {`${locked ? 0 : count}/${total}`}
                    </Text>
                </Row>

                {(hasPetals || hasTitle || done || locked || hasAction) && (
                    <Row gap="3" justify="between">
                        <InsetContext.Provider value={!auroraShown && isInsetSurface(background)}>
                            <Row gap="2" style={styles.chips}>
                                {hasPetals && <GainChip amount={petals} unit="petals" />}
                                {hasTitle && <TitleMedal title={titleGained} tone={locked ? "muted" : "gain"} />}
                                {done && <Badge text={TEXTS.done} variant="success" tinted iconName="check" />}
                                {locked && <Badge text={TEXTS.locked} variant="neutral" iconName="lock" />}
                            </Row>
                        </InsetContext.Provider>
                        {hasAction && <Button label={actionLabel} onPress={onPress} />}
                    </Row>
                )}
            </Stack>
        </Card>
    )
}

type QuestBarProps = { count: number; total: number; done: boolean }

/**
 * L'avancement d'une étape : la barre segmentée du livre tant que les pastilles se comptent,
 * une piste continue au-delà (« 120 minutes d'activité » ne fait pas 120 pastilles).
 */
function QuestBar(props: QuestBarProps) {
    const { count, total, done } = props
    const color = done ? "success" : "role"

    if (total <= QUEST_STEPS_MAX) {
        return (
            <SegmentedBar max={total} height={STEP_BAR_HEIGHT} gap="1" segments={stepSegments(total, count, color)} />
        )
    }

    return (
        <ProgressBar
            value={total > 0 ? (count / total) * 100 : 0}
            color={color}
            trackColor="border"
            height={STEP_BAR_HEIGHT}
            radius="full"
        />
    )
}

const styles = StyleSheet.create(() => ({
    body: { flex: 1, flexShrink: 1, minWidth: 0 },
    /** Sa tête garde sa taille quand la phrase s'allonge. */
    champion: { flexShrink: 0 },
    /** L'estompe d'une image, pas d'un texte : celui d'une étape à venir. */
    dimmed: { opacity: dimming.opacity },
    chevron: { flexShrink: 0, alignSelf: "center" },
    bar: { flex: 1 },
    /** Ce que l'étape rapporte et où elle en est : les chips passent à la ligne, le bouton reste à droite. */
    chips: { flex: 1, flexShrink: 1, flexWrap: "wrap" },
}))
